import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Menu, X, CheckCircle, AlertCircle, Bell } from 'lucide-react';
import './InfoPage.css';
import './SMSOptInPage.css';

const SMSOptInPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!consent) {
      setError('You must agree to receive SMS notifications to continue');
      return;
    }

    // Here you can add your API call to save the opt-in data
    console.log('SMS Opt-in submitted:', { phone, consent });
    // Example: await api.saveOptIn({ phone, consent });

    setSubmitted(true);
    setError('');
  };

  return (
    <div className="info-page sms-optin-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <MessageSquare className="badge-icon" />
              <span>SMS NOTIFICATIONS</span>
            </div>
            <h1 className="hero-title">
              Stay <span className="gradient-text">Connected</span>
            </h1>
            <p className="hero-subtitle">
              Get instant project updates, appointment reminders, and important construction alerts via SMS
            </p>
          </motion.div>
        </div>
      </section>

      {submitted ? (
        <section className="info-content">
          <div className="content-container">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="success-container"
            >
              <div className="success-icon-wrapper">
                <CheckCircle className="success-icon-large" />
              </div>
              <h2 className="success-title">You're All Set!</h2>
              <p className="success-message">
                Thank you for subscribing to SMS notifications from HomeQuest Tech. You'll start receiving project updates, appointment reminders, and important construction alerts on your phone.
              </p>
              <div className="success-info">
                <Bell className="info-icon" />
                <p>
                  <strong>What's Next?</strong><br />
                  You'll receive a confirmation text shortly. Reply STOP at any time to unsubscribe.
                </p>
              </div>
              <button onClick={() => navigate('/')} className="cta-button">
                Return to Home
              </button>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="info-content">
          <div className="content-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="form-container"
            >
              <div className="form-intro">
                <h2>Why Subscribe to SMS Notifications?</h2>
                <div className="benefits-grid">
                  <div className="benefit-card">
                    <div className="benefit-icon">📱</div>
                    <h3>Instant Updates</h3>
                    <p>Get real-time project updates delivered directly to your phone</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">📅</div>
                    <h3>Appointment Reminders</h3>
                    <p>Never miss an important meeting or site visit</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">💰</div>
                    <h3>Budget Alerts</h3>
                    <p>Stay informed about budget changes and approval requests</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">🔔</div>
                    <h3>Critical Notifications</h3>
                    <p>Receive urgent alerts about weather, safety, or delays</p>
                  </div>
                </div>
              </div>

              <div className="optin-form-wrapper">
                <h2>Subscribe to SMS Notifications</h2>
                <form onSubmit={handleSubmit} className="optin-form">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={handlePhoneChange}
                      placeholder="(555) 123-4567"
                      maxLength={14}
                      required
                      className={error ? 'error' : ''}
                    />
                  </div>

                  <div className="consent-section">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="sms-consent"
                        checked={consent}
                        onChange={(e) => {
                          setConsent(e.target.checked);
                          setError('');
                        }}
                        required
                      />
                      <label htmlFor="sms-consent">
                        <div className="consent-label">
                          By providing your phone number, you agree to receive SMS notifications about
                          project updates, appointment reminders, and construction-related alerts from{' '}
                          <strong>HomeQuest Tech</strong>. Message frequency varies. Message & data rates may apply.
                          You can reply STOP to opt out at any time.{' '}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        </div>
                      </label>
                    </div>
                  </div>

                  {error && (
                    <div className="error-box">
                      <AlertCircle className="error-icon" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="message-info-box">
                    <h3>What You Need to Know</h3>
                    <ul>
                      <li><strong>Types of messages:</strong> Project updates, appointment reminders, budget alerts, vendor notifications, weather alerts, and safety notices</li>
                      <li><strong>Frequency:</strong> Message frequency may vary based on your project activity and urgency of notifications</li>
                      <li><strong>Costs:</strong> Message and data rates may apply from your mobile carrier</li>
                      <li><strong>Opt-out:</strong> Reply STOP to any message to unsubscribe at any time</li>
                      <li><strong>Help:</strong> Reply HELP to any message for assistance</li>
                    </ul>
                  </div>

                  <button type="submit" className="submit-btn" disabled={!phone || !consent}>
                    Subscribe to SMS Notifications
                  </button>
                </form>

                <div className="privacy-commitment">
                  <h3>Our Privacy Commitment</h3>
                  <p>
                    <strong>Your privacy matters to us.</strong> HomeQuest Tech will never share, sell, or rent your mobile phone number or SMS opt-in information with third parties, affiliates, or other companies for marketing or promotional purposes. Your mobile information is used solely for providing you with our construction management services.
                  </p>
                  <p>
                    For more details, please read our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/terms')}>Terms</a>
              <a onClick={() => navigate('/privacy')}>Privacy</a>
              <a onClick={() => navigate('/security')}>Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SMSOptInPage;
